import type { Bounds2D, Point2D, ViewportCameraBounds } from '../models/types';
import type { Viewport } from 'pixi-viewport';

type ClampViewportPositionParams = {
  position: Point2D;
  scale: number;
  bounds: Bounds2D;
  screenWidth: number;
  screenHeight: number;
  isMobile: boolean;
};

type ClampViewportDragPositionParams = Omit<ClampViewportPositionParams, 'isMobile'> & {
  isMobile?: boolean;
};

const getCameraPaddingPixels = (isMobile: boolean) => ({
  left: isMobile ? 16 : 24,
  right: isMobile ? 16 : 24,
  top: isMobile ? 16 : 24,
  bottom: isMobile ? 16 : 24,
});

const getBottomBoundaryPaddingPixels = (isMobile: boolean) => (isMobile ? 64 : 48);

const clampAxisPosition = ({
  position,
  scale,
  min,
  max,
  screenSize,
  endPadding = 0,
}: {
  position: number;
  scale: number;
  min: number;
  max: number;
  screenSize: number;
  endPadding?: number;
}) => {
  const contentSize = Math.max(1, (max - min) * scale);
  const center = screenSize / 2 - ((min + max) / 2) * scale;

  if (contentSize <= screenSize) return center;

  const minPosition = screenSize - endPadding - max * scale;
  const maxPosition = -min * scale;

  return Math.max(minPosition, Math.min(maxPosition, position));
};

export const buildCameraFitScale = (
  bounds: Bounds2D,
  screenWidth: number,
  screenHeight: number,
  isMobile: boolean
) => {
  const paddingPx = getCameraPaddingPixels(isMobile);
  const availableWidth = Math.max(1, screenWidth - paddingPx.left - paddingPx.right);
  const availableHeight = Math.max(1, screenHeight - paddingPx.top - paddingPx.bottom);
  const contentWidth = Math.max(1, bounds.maxX - bounds.minX);
  const contentHeight = Math.max(1, bounds.maxY - bounds.minY);

  return Math.min(availableWidth / contentWidth, availableHeight / contentHeight);
};

export const clampViewportPosition = ({
  position,
  scale,
  bounds,
  screenWidth,
  screenHeight,
  isMobile,
}: ClampViewportPositionParams): Point2D => {
  return {
    x: clampAxisPosition({
      position: position.x,
      scale,
      min: bounds.minX,
      max: bounds.maxX,
      screenSize: screenWidth,
    }),
    y: clampAxisPosition({
      position: position.y,
      scale,
      min: bounds.minY,
      max: bounds.maxY,
      screenSize: screenHeight,
      endPadding: getBottomBoundaryPaddingPixels(isMobile),
    }),
  };
};

export const clampViewportPinchPosition = ({
  position,
  scale,
  bounds,
  screenWidth,
  screenHeight,
  isMobile,
}: ClampViewportPositionParams): Point2D => {
  return {
    x: clampAxisPosition({
      position: position.x,
      scale,
      min: bounds.minX,
      max: bounds.maxX,
      screenSize: screenWidth,
    }),
    y: clampAxisPosition({
      position: position.y,
      scale,
      min: bounds.minY,
      max: bounds.maxY,
      screenSize: screenHeight,
      endPadding: getBottomBoundaryPaddingPixels(isMobile),
    }),
  };
};

export const clampViewportDragPosition = ({
  position,
  scale,
  bounds,
  screenWidth,
  screenHeight,
  isMobile = false,
}: ClampViewportDragPositionParams): Point2D => {
  return {
    x: clampAxisPosition({
      position: position.x,
      scale,
      min: bounds.minX,
      max: bounds.maxX,
      screenSize: screenWidth,
    }),
    y: clampAxisPosition({
      position: position.y,
      scale,
      min: bounds.minY,
      max: bounds.maxY,
      screenSize: screenHeight,
      endPadding: getBottomBoundaryPaddingPixels(isMobile),
    }),
  };
};

export const buildCenteredViewportPosition = (
  worldPoint: Point2D,
  scale: number,
  screenWidth: number,
  screenHeight: number
): Point2D => ({
  x: screenWidth / 2 - worldPoint.x * scale,
  y: screenHeight / 2 - worldPoint.y * scale,
});

export const buildViewportPositionForScreenAnchor = (
  worldPoint: Point2D,
  screenPoint: Point2D,
  scale: number
): Point2D => ({
  x: screenPoint.x - worldPoint.x * scale,
  y: screenPoint.y - worldPoint.y * scale,
});

export const setViewportCamera = (viewport: Viewport, position: Point2D, scale: number) => {
  viewport.scale.set(scale, scale);
  viewport.position.set(position.x, position.y);
};

export const applyViewportZoomAtScreenPoint = (
  viewport: Viewport,
  screenPoint: Point2D,
  nextScale: number,
  cameraBounds: ViewportCameraBounds,
  isMobile: boolean
) => {
  const worldPoint = viewport.toWorld(screenPoint);
  const unclampedPosition = buildViewportPositionForScreenAnchor(
    worldPoint,
    screenPoint,
    nextScale
  );
  const clampedPosition = clampViewportPosition({
    position: unclampedPosition,
    scale: nextScale,
    bounds: cameraBounds.bounds,
    screenWidth: cameraBounds.screenWidth,
    screenHeight: cameraBounds.screenHeight,
    isMobile,
  });

  setViewportCamera(viewport, clampedPosition, nextScale);
};
